import React, { useEffect, useRef } from "react";
import EditorJS from "@editorjs/editorjs";

// Import Editor.js plugins for various block types
import Header from "@editorjs/header";
import List from "@editorjs/list";
import Embed from "@editorjs/embed";
import Table from "@editorjs/table";
import Marker from "@editorjs/marker";
import Quote from "@editorjs/quote";
import Delimiter from "@editorjs/delimiter";
import Warning from "@editorjs/warning";
import Paragraph from "@editorjs/paragraph";
import Checklist from "@editorjs/checklist";
import ChartBlock from "./plugins/editorjs_chartjs_plugin"; // Custom chart plugin
import ImageBlock from "./plugins/editorjs_custom_image_plugin";
import { OutputData } from "@editorjs/editorjs"; // Editor.js output data type
import { ChartDisplayProps } from "../../interfaces/Charts";
import { createEditorImageUploader } from "./utils/EditorImageUploader";

interface EditorProps {
  initialContent: string; // Initial content to populate the editor
  plotsData?: ChartDisplayProps; // Data containing chart configurations
  onSave?: (data: OutputData) => void; // Callback function triggered on content save
  isReadOnly?: boolean; // Indicates if the editor content is editable
  onBlockSelect?: (blockId: string) => void;
}

const ArkEditor: React.FC<EditorProps> = ({
  initialContent,
  onSave,
  isReadOnly = false,
}) => {
  const editorContainer = useRef<HTMLDivElement>(null); // Reference to the editor container
  const editorInstance = useRef<EditorJS | null>(null); // Reference to the Editor.js instance
  const data = JSON.parse(initialContent);
  const uploader = createEditorImageUploader();

  /**
   * Initializes the Editor.js instance with the specified tools and data.
   * Sets up an `onChange` event to automatically save data when changes are made.
   */
  useEffect(() => {
    if (editorContainer.current && !editorInstance.current) {
      const tools: { [toolName: string]: object } = {
        header: {
          class: Header,
          inlineToolbar: true, // Enable inline toolbar for headers
        },
        paragraph: {
          class: Paragraph,
          inlineToolbar: true, // Enable inline toolbar for paragraphs
          config: {
            preserveBlank: true,
          },
        },
        list: {
          class: List,
          inlineToolbar: true, // Enable inline toolbar for list items
          config: {
            defaultStyle: "ordered", // Can be 'unordered' or 'ordered' (no checklist)
          },
        },
        embed: Embed,
        table: Table,
        marker: {
          class: Marker,
          inlineToolbar: true, // Allow inline formatting within highlighted text
        },
        quote: {
          class: Quote,
          inlineToolbar: true, // Allow inline tools for quotes
        },
        delimiter: Delimiter,
        warning: Warning,
        checklist: {
          class: Checklist,
          inlineToolbar: true, // Enable inline tools within checklist items
          config: {
            readOnly: isReadOnly,
          },
        },
        image: {
          class: ImageBlock,
          config: {
            uploader: {
              uploadByFile: (file: File) => uploader.uploadByFile(file), // AWS Upload
            },
          },
          readOnly: isReadOnly,
        },
        chart: ChartBlock,
      };

      editorInstance.current = new EditorJS({
        readOnly: isReadOnly,
        holder: editorContainer.current, // Element to initialize Editor.js in
        tools: tools, // Provide all tools for block editing
        data: data, // Initial content for the editor
        onChange: async () => {
          const savedData = await editorInstance.current!.save(); // Save current editor content
          if (onSave) {
            onSave(savedData); // Trigger the provided onSave callback with the saved data
          }
        },
      });
    }
  }, []);

  return <div className="" id="custom-editor" ref={editorContainer}></div>;
};

export default ArkEditor;
